// DevHub UI kit — star toggle button.
//
// The favorite/unfavorite control extracted from DocCard and CatalogEntityCard so
// cards AND tiles share one implementation. Backed by Backstage's built-in
// localStorage-based useStarredEntities (the same store the header dropdown reads).
//
// It is meant to sit INSIDE a card <Link>, so the click is swallowed
// (preventDefault + stopPropagation) to avoid navigating when toggling a star.
import { Entity } from '@backstage/catalog-model';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { kitMessages } from '../../i18n/kit';

export interface StarToggleProps {
  entity: Entity;
  /** Extra class(es) appended to the base `devhub-docs-card-star` styling. */
  className?: string;
}

export const StarToggle = ({ entity, className }: StarToggleProps) => {
  const { t } = useTranslationRef(kitMessages);
  const { toggleStarredEntity, isStarredEntity } = useStarredEntities();
  // useStarredEntities derives the entityRef (kind:namespace/name) from the entity
  // itself — the same ref the catalog card grid and docs cards star.
  const starred = isStarredEntity(entity);

  // The positioning/styling class lives on a real <span> wrapper, NOT on the
  // <odx-icon-button>: React 18 does not map `className` → `class` on custom
  // (hyphenated) elements — it emits a literal `classname` attribute the CSS can
  // never match. The span's className becomes a real `class`, so the corner
  // positioning in EntityCard.css actually applies. The odx-icon-button keeps its
  // own default ghost styling and carries the interaction/aria.
  return (
    <span
      className={`devhub-docs-card-star ${starred ? 'is-starred' : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      <odx-icon-button
        type="button"
        variant="ghost"
        size="sm"
        icon={starred ? 'core::favorite-solid' : 'core::favorite'}
        aria-pressed={starred}
        title={starred ? t('card.unstar') : t('card.star')}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          toggleStarredEntity(entity);
        }}
      />
    </span>
  );
};
